/**
 * RouteTracker Component
 * Automatically tracks page views when routes change
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAnalytics } from '../../hooks/useAnalytics';

/** Map of route patterns to human-readable page names */
const ROUTE_NAMES: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/pending-instructions/payments': 'Pending Payments',
  '/pending-instructions/instructions': 'Pending Instructions',
  '/users': 'Users & Accounts',
  '/users/client-migrations': 'Client Migration Campaigns',
  '/client-account/wallet-accounts': 'Wallet Accounts',
  '/client-account/client-deposit-accounts': 'Client Deposit Accounts',
  '/instructions/all': 'All Instructions',
  '/instructions/fiat-withdraws': 'Fiat Withdrawals',
  '/instructions/fiat-deposits': 'Fiat Deposits',
  '/instructions/transfers': 'Transfers',
  '/instructions/exchanges': 'Exchanges',
  '/digital-custody/deposits': 'Digital Deposits',
  '/digital-custody/withdraws': 'Digital Withdrawals',
  '/digital-custody/address-screening': 'Address Screening',
  '/compliance/cases': 'Compliance Cases',
  '/compliance/kyt-screening': 'KYT Screening',
  '/cms/cards': 'Cards',
  '/cms/transactions': 'Card Transactions',
  '/cms/card-account': 'Card Accounts',
  '/earn/subscriptions': 'Earn Subscriptions',
  '/reporting/standard': 'Standard Reports',
  '/reporting/accounting': 'Accounting Reports',
  '/accounting/journal-entries': 'Journal Entries',
  '/transaction-monitoring/fraud-transactions': 'Fraud Transactions',
  '/rate-source/configuration': 'Rate Configuration',
  '/super/operators': 'Operators',
  '/login': 'Login'
};

/**
 * Get a human-readable page name from a route path
 * @param pathname - The route pathname
 * @returns Human-readable page name
 */
function getPageName(pathname: string): string {
  // Check for exact match first
  if (ROUTE_NAMES[pathname]) {
    return ROUTE_NAMES[pathname];
  }

  // Check for pattern matches (e.g., /users/:id -> User Details)
  const patterns: Array<{ pattern: RegExp; name: string }> = [
    { pattern: /^\/users\/client-migrations\/[^/]+$/, name: 'Client Migration Campaign' },
    { pattern: /^\/users\/[^/]+$/, name: 'User Details' },
    { pattern: /^\/instructions\/fiat-withdraws\/[^/]+$/, name: 'Fiat Withdrawal Details' },
    { pattern: /^\/instructions\/fiat-deposits\/[^/]+$/, name: 'Fiat Deposit Details' },
    { pattern: /^\/instructions\/transfers\/[^/]+$/, name: 'Transfer Details' },
    { pattern: /^\/instructions\/exchanges\/[^/]+$/, name: 'Exchange Details' },
    { pattern: /^\/instructions\/general-instruction\/[^/]+$/, name: 'General Instruction Details' },
    { pattern: /^\/digital-custody\/deposits\/[^/]+$/, name: 'Digital Deposit Details' },
    { pattern: /^\/digital-custody\/withdraws\/[^/]+$/, name: 'Digital Withdrawal Details' },
    { pattern: /^\/digital-custody\/screening-history\/[^/]+$/, name: 'Screening Details' },
    { pattern: /^\/compliance\/cases\/[^/]+$/, name: 'Case Details' },
    { pattern: /^\/compliance\/kyt-screening\/[^/]+$/, name: 'KYT Details' },
    { pattern: /^\/cms\/cards\/[^/]+\/[^/]+$/, name: 'Card Details' },
    { pattern: /^\/cms\/card-accounts\/[^/]+\/[^/]+$/, name: 'Card Account Details' },
    { pattern: /^\/cms\/transactions\/[^/]+$/, name: 'Transaction Details' },
    { pattern: /^\/earn\/subscriptions\/[^/]+$/, name: 'Subscription Details' },
    { pattern: /^\/earn\/redemptions\/[^/]+$/, name: 'Redemption Details' },
    { pattern: /^\/name-check\/[^/]+$/, name: 'Name Check Details' },
    { pattern: /^\/card-fraud-cases\/[^/]+$/, name: 'Card Fraud Case Details' },
    { pattern: /^\/support-center\/[^/]+\/[^/]+$/, name: 'Support Request Details' }
  ];

  for (const { pattern, name } of patterns) {
    if (pattern.test(pathname)) {
      return name;
    }
  }

  // Generate a name from the path
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) {
    return 'Home';
  }

  // Convert path to title case
  const lastSegment = segments[segments.length - 1];
  return lastSegment
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

interface RouteTrackerProps {
  /** Optional prefix for page names */
  pageNamePrefix?: string;
}

/**
 * RouteTracker Component
 * Place this component once in your app (typically in the main layout)
 * to automatically track page views when routes change
 */
export function RouteTracker({ pageNamePrefix = '' }: RouteTrackerProps) {
  const location = useLocation();
  const { trackPageView, isEnabled } = useAnalytics();
  const previousPathRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    const currentPath = location.pathname;

    // Only track if path actually changed (not just search params)
    if (previousPathRef.current === currentPath) {
      return;
    }

    previousPathRef.current = currentPath;

    // Get page name and track
    const pageName = getPageName(currentPath);
    const fullPageName = pageNamePrefix ? `${pageNamePrefix} - ${pageName}` : pageName;

    // Small delay to ensure the page has rendered
    const timeoutId = setTimeout(() => {
      trackPageView(fullPageName);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [location.pathname, trackPageView, isEnabled, pageNamePrefix]);

  // This component doesn't render anything
  return null;
}

/**
 * Export page name getter for use in other components
 */
export { getPageName };

export default RouteTracker;
